'use client';

import { useEffect, useRef, useState } from 'react';

import { logger } from '@onyx/logger';

import type { AndroidMessage, MessageValues } from './types';

export const useNativeData = (
  onMessage?: (message: AndroidMessage) => void
) => {
  const portRef = useRef<MessagePort | null>(null);
  const [isEnabled, setIsEnabled] = useState(false);

  useEffect(() => {
    const setupPort = (port: MessagePort) => {
      if (portRef.current) {
        portRef.current.onmessage = null;
        portRef.current.close();
      }

      portRef.current = port;

      portRef.current.onmessage = (event: MessageEvent) => {
        // eslint-disable-next-line functional/no-let
        let data = {
          key: 'message',
          value: event.data,
        } as AndroidMessage;

        // eslint-disable-next-line functional/no-try-statement
        try {
          data = JSON.parse(event.data);
        } catch {
          // A JSON.parse error message quotes the input it failed on, and
          // native messages can carry user and device identifiers.
          logger.error({
            event: 'native_data_parse_failed',
            message: 'Failed to parse message from Android',
          });
        }

        if (data.key === 'ping') {
          portRef.current?.postMessage('pong');
        }

        onMessage?.(data);
      };

      setIsEnabled(true);
    };

    const onWindowMessage = (event: MessageEvent) => {
      if (
        !event.origin.startsWith('android-app://') ||
        !event.ports ||
        !event.ports[0]
      ) {
        return;
      }

      setupPort(event.ports[0]);
    };

    window.addEventListener('message', onWindowMessage);

    return () => {
      window.removeEventListener('message', onWindowMessage);
    };
  }, [onMessage]);

  return {
    postMessage: (value: MessageValues) => {
      portRef.current?.postMessage(value);
    },
    enabled: isEnabled,
  };
};
